// Slice 42 — right-pane detail for the selected feedback row. Header
// (avatar + meta + status-segmented + tag editor), body, screenshot,
// meta grid, GDPR redact stripe, reply composer.

import { useEffect, useState } from "react";
import {
  FEEDBACK_STATUSES,
  FeedbackApi,
  type FeedbackDetail,
  type FeedbackStatus,
  type FeedbackThreadMessage,
  REDACTABLE_FEEDBACK_FIELDS,
  type RedactableFeedbackField,
} from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCheckbox, HqIconButton, HqInput, HqModal, Icons } from "../../ui";
import { MonoId } from "../licenses/MonoId";
import { fmtRelative, parseSystemInfo } from "./format";

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: "New",
  triaged: "Triaged",
  in_progress: "In progress",
  closed: "Closed",
};

export interface FeedbackDetailPaneProps {
  row: FeedbackDetail | null;
  loading: boolean;
  onUpdate: (row: FeedbackDetail) => void;
}

export function FeedbackDetailPane({ row, loading, onUpdate }: FeedbackDetailPaneProps) {
  if (loading && !row) {
    return <EmptyState message="Loading…" />;
  }
  if (!row) {
    return <EmptyState message="Select a feedback row to read it." />;
  }
  return <Loaded row={row} onUpdate={onUpdate} />;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        color: "var(--hq-text-dim)",
        fontSize: 13,
        padding: 24,
      }}
    >
      {message}
    </div>
  );
}

function Loaded({
  row,
  onUpdate,
}: {
  row: FeedbackDetail;
  onUpdate: (row: FeedbackDetail) => void;
}) {
  const { toast } = useShell();
  const [tagInput, setTagInput] = useState("");
  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [redactOpen, setRedactOpen] = useState(false);
  const [redactFields, setRedactFields] = useState<Set<RedactableFeedbackField>>(new Set());

  // Clear the per-row scratch state whenever the selected row changes.
  // `void row.id` keeps biome happy that the id is load-bearing.
  useEffect(() => {
    void row.id;
    setTagInput("");
    setReply("");
    setRedactFields(new Set());
  }, [row.id]);

  async function patch(input: { status?: FeedbackStatus; tags?: string[] }) {
    const res = await FeedbackApi.update(row.id, input);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    if (res.data) onUpdate(res.data);
  }

  async function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if (row.tags.includes(t)) {
      setTagInput("");
      return;
    }
    setTagInput("");
    await patch({ tags: [...row.tags, t] });
  }

  async function removeTag(t: string) {
    await patch({ tags: row.tags.filter((x) => x !== t) });
  }

  async function send() {
    const text = reply.trim();
    if (text.length < 2) {
      toast("Reply too short.", "warn");
      return;
    }
    setReplyBusy(true);
    const res = await FeedbackApi.reply(row.id, text);
    setReplyBusy(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setReply("");
    // Optimistically append the new message + bump status to in_progress
    // (mirrors the server-side state transition in admin-feedback.ts).
    onUpdate({
      ...row,
      messages: [...row.messages, res.data],
      lastMessageAt: res.data.createdAt,
      status: row.status === "closed" ? "closed" : "in_progress",
    });
    toast("Reply sent.", "success");
  }

  async function redact() {
    if (redactFields.size === 0) return;
    const res = await FeedbackApi.redact(row.id, Array.from(redactFields));
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setRedactOpen(false);
    setRedactFields(new Set());
    // Re-fetch detail since the response only echoes the field list.
    const fresh = await FeedbackApi.get(row.id);
    if (fresh.ok) onUpdate(fresh.data);
    toast("Redaction applied + audited.", "success");
  }

  const sysInfo = parseSystemInfo(row.systemInfo);
  const wasRedacted = row.redactedFields.length > 0;
  return (
    <div
      className="hq-scroll"
      style={{ display: "flex", flexDirection: "column", overflow: "auto", maxHeight: 720 }}
    >
      <Header row={row} />
      <StatusBar
        status={row.status}
        onStatusChange={(s) => void patch({ status: s })}
        tags={row.tags}
        onRemoveTag={(t) => void removeTag(t)}
        tagInput={tagInput}
        onTagInput={setTagInput}
        onAddTag={addTag}
      />
      <Thread messages={row.messages} redactedFields={row.redactedFields} fallbackBody={row.body} />
      {row.screenshotBase64 && <Screenshot base64={row.screenshotBase64} />}
      {!row.screenshotBase64 && row.redactedFields.includes("screenshotBase64") && (
        <RedactedNotice field="screenshotBase64" />
      )}
      <MetaGrid row={row} sysInfo={sysInfo} />
      <GdprStrip
        wasRedacted={wasRedacted}
        redactedFields={row.redactedFields}
        redactedBy={row.redactedBy}
        redactedAt={row.redactedAt}
        consent={!!sysInfo}
        onOpenRedact={() => setRedactOpen(true)}
      />
      <ReplyComposer
        value={reply}
        onChange={setReply}
        busy={replyBusy}
        onSend={() => void send()}
      />

      <HqModal
        open={redactOpen}
        onClose={() => setRedactOpen(false)}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icons.EyeOff width={14} height={14} sw={1.7} style={{ color: "var(--hq-warn)" }} />
            Redact feedback fields
          </span>
        }
        width={440}
        footer={
          <>
            <HqButton variant="ghost" onClick={() => setRedactOpen(false)}>
              Cancel
            </HqButton>
            <HqButton
              variant="danger"
              leadingIcon={Icons.EyeOff}
              onClick={() => void redact()}
              disabled={redactFields.size === 0}
            >
              Redact &amp; audit
            </HqButton>
          </>
        }
      >
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 12.5,
            color: "var(--hq-text-muted)",
            lineHeight: 1.55,
          }}
        >
          Listed fields are nulled <strong>in place</strong>; the redaction row (who, when, which
          fields) lands in the audit log. This is irreversible — keep originals in your DSR response
          if relevant.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {REDACTABLE_FEEDBACK_FIELDS.map((f) => (
            <label
              key={f}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                background: "var(--hq-bg-input)",
                border: "1px solid var(--hq-border-input)",
                borderRadius: "var(--hq-radius-md)",
                cursor: "pointer",
              }}
            >
              <HqCheckbox
                checked={redactFields.has(f)}
                onChange={(next) => {
                  const set = new Set(redactFields);
                  if (next) set.add(f);
                  else set.delete(f);
                  setRedactFields(set);
                }}
                ariaLabel={f}
              />
              <span style={{ fontFamily: "var(--hq-font-mono)", fontSize: 11 }}>{f}</span>
              {row.redactedFields.includes(f) && (
                <HqBadge tone="warn" size="sm">
                  already redacted
                </HqBadge>
              )}
            </label>
          ))}
        </div>
      </HqModal>
    </div>
  );
}

function Header({ row }: { row: FeedbackDetail }) {
  const cust = row.customer;
  // Pick the most-useful display label: name > masked email > license > "anon".
  const headline = cust?.displayName?.trim() || cust?.emailMasked || row.licenseId || "anonymous";
  return (
    <div
      style={{
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        borderBottom: "1px solid var(--hq-border)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--hq-text)" }}>{headline}</span>
        {cust && (
          <HqBadge tone={cust.tier} size="sm">
            {cust.tier}
          </HqBadge>
        )}
        {cust?.betaProgram && (
          <HqBadge tone="warn" size="sm">
            beta-program
          </HqBadge>
        )}
        {cust && !cust.feedbackEnabled && (
          <HqBadge tone="danger" size="sm">
            feedback disabled
          </HqBadge>
        )}
        {cust?.tombstonedAt && (
          <HqBadge tone="danger" size="sm">
            tombstoned
          </HqBadge>
        )}
        <HqBadge tone="neutral" size="sm">
          v{row.vyvernVersion}
        </HqBadge>
      </div>
      {cust && (
        <div
          className="hq-mono"
          style={{
            fontSize: 10.5,
            color: "var(--hq-text-dim)",
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {cust.emailMasked && cust.displayName && (
            <span title="masked email">{cust.emailMasked}</span>
          )}
          {cust.country && <span title="country">{cust.country}</span>}
          <span title="customer id">cust {cust.id.slice(0, 12)}…</span>
        </div>
      )}
      <div
        className="hq-mono"
        style={{
          fontSize: 10.5,
          color: "var(--hq-text-dim)",
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <span>received {fmtRelative(row.receivedAt)}</span>
        <span>
          license <MonoId value={row.licenseId ?? "anon"} short={16} color="var(--hq-text-dim)" />
        </span>
        <span>ticket {row.id.slice(0, 12)}…</span>
      </div>
    </div>
  );
}

function StatusBar({
  status,
  onStatusChange,
  tags,
  onRemoveTag,
  tagInput,
  onTagInput,
  onAddTag,
}: {
  status: FeedbackStatus;
  onStatusChange: (s: FeedbackStatus) => void;
  tags: string[];
  onRemoveTag: (t: string) => void;
  tagInput: string;
  onTagInput: (v: string) => void;
  onAddTag: () => Promise<void>;
}) {
  return (
    <div
      style={{
        padding: "12px 18px",
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        borderBottom: "1px solid var(--hq-border)",
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          padding: 3,
          gap: 2,
          background: "var(--hq-bg-input)",
          borderRadius: "var(--hq-radius-lg)",
          border: "1px solid var(--hq-border-input)",
        }}
      >
        {FEEDBACK_STATUSES.map((s) => {
          const active = s === status;
          return (
            <button
              key={s}
              type="button"
              onClick={() => onStatusChange(s)}
              style={{
                padding: "0 10px",
                height: 26,
                borderRadius: "var(--hq-radius-md)",
                background: active ? "var(--hq-bg-active)" : "transparent",
                color: active ? "var(--hq-text)" : "var(--hq-text-muted)",
                fontSize: 11.5,
                border: "none",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {STATUS_LABEL[s]}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {tags.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onRemoveTag(t)}
            title="Remove tag"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 6px",
              borderRadius: "var(--hq-radius-md)",
              background: "var(--hq-accent-soft)",
              color: "var(--hq-accent)",
              border: "1px solid rgba(34,211,238,.25)",
              fontFamily: "var(--hq-font-mono)",
              fontSize: 10.5,
              cursor: "pointer",
            }}
          >
            {t}
            <Icons.X width={10} height={10} sw={2} />
          </button>
        ))}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onAddTag();
          }}
          style={{ width: 160 }}
        >
          <HqInput
            placeholder="add tag + Enter"
            size="sm"
            value={tagInput}
            onChange={(e) => onTagInput(e.target.value)}
          />
        </form>
      </div>
    </div>
  );
}

/**
 * Thread bubble list — customer messages right-aligned, staff replies
 * left-aligned. The first user message inherits the legacy
 * `feedback.body` field; if the row predates the threading migration
 * AND was never re-touched (so `messages` is empty), we render the
 * legacy body as the sole bubble so old tickets keep displaying.
 */
function Thread({
  messages,
  redactedFields,
  fallbackBody,
}: {
  messages: FeedbackThreadMessage[];
  redactedFields: RedactableFeedbackField[];
  fallbackBody: string;
}) {
  const isBodyRedacted = redactedFields.includes("body");
  // Legacy guard: empty thread + body present = pre-migration row.
  const effective: FeedbackThreadMessage[] =
    messages.length === 0
      ? [
          {
            id: "legacy",
            sender: "user",
            senderName: null,
            body: isBodyRedacted ? "[REDACTED]" : fallbackBody,
            createdAt: 0,
            transport: "in_app",
            deliveredAt: null,
            readByUserAt: null,
            readByStaffAt: null,
          },
        ]
      : messages;

  return (
    <div
      style={{
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {effective.map((m) => (
        <ThreadBubble key={m.id} msg={m} />
      ))}
    </div>
  );
}

function ThreadBubble({ msg }: { msg: FeedbackThreadMessage }) {
  const isUser = msg.sender === "user";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        gap: 4,
        maxWidth: "84%",
        alignSelf: isUser ? "flex-end" : "flex-start",
      }}
    >
      <div
        style={{
          background: isUser ? "var(--hq-bg-input)" : "var(--hq-accent-soft)",
          color: "var(--hq-text)",
          border: "1px solid",
          borderColor: isUser ? "var(--hq-border)" : "rgba(99,102,241,.35)",
          borderRadius: "var(--hq-radius-lg)",
          borderBottomRightRadius: isUser ? 4 : "var(--hq-radius-lg)",
          borderBottomLeftRadius: isUser ? "var(--hq-radius-lg)" : 4,
          padding: "10px 12px",
          fontSize: 13,
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {msg.body}
      </div>
      <div
        className="hq-mono"
        style={{ fontSize: 10, color: "var(--hq-text-dim)", display: "flex", gap: 6 }}
      >
        <span>{isUser ? "customer" : (msg.senderName ?? "staff")}</span>
        {msg.createdAt > 0 && <span>· {fmtRelative(msg.createdAt)}</span>}
        {msg.transport === "email" && <span>· email</span>}
        {!isUser && msg.readByUserAt && <span>· read</span>}
      </div>
    </div>
  );
}

function Screenshot({ base64 }: { base64: string }) {
  const src = base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`;
  return (
    <div style={{ padding: "0 18px 16px" }}>
      <div
        style={{
          border: "1px solid var(--hq-border)",
          borderRadius: "var(--hq-radius-lg)",
          overflow: "hidden",
          background: "var(--hq-bg-input)",
        }}
      >
        <img
          src={src}
          alt="User-submitted screenshot"
          style={{ display: "block", maxWidth: "100%", height: "auto" }}
        />
      </div>
    </div>
  );
}

function RedactedNotice({ field }: { field: string }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: "var(--hq-radius-md)",
        background: "var(--hq-warn-soft)",
        color: "var(--hq-warn)",
        border: "1px solid rgba(251,191,36,.25)",
        fontFamily: "var(--hq-font-mono)",
        fontSize: 10.5,
        letterSpacing: ".08em",
        textTransform: "uppercase",
      }}
    >
      <Icons.EyeOff width={12} height={12} sw={1.8} />
      {field} redacted
    </div>
  );
}

function MetaGrid({
  row,
  sysInfo,
}: {
  row: FeedbackDetail;
  sysInfo: ReturnType<typeof parseSystemInfo>;
}) {
  const isInfoRedacted = row.redactedFields.includes("systemInfo");
  return (
    <div
      style={{
        padding: "0 18px 16px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: 10,
      }}
    >
      <MetaCell
        label="License"
        value={row.licenseId ? <MonoId value={row.licenseId} short={12} /> : "—"}
      />
      <MetaCell label="Vyvern" value={row.vyvernVersion} mono />
      <MetaCell
        label="OS"
        value={isInfoRedacted ? "—" : sysInfo?.os || "—"}
        mono
        redacted={isInfoRedacted}
      />
      <MetaCell
        label="Instances"
        value={isInfoRedacted ? "—" : (sysInfo?.instanceCount?.toString() ?? "—")}
        mono
        redacted={isInfoRedacted}
      />
    </div>
  );
}

function MetaCell({
  label,
  value,
  mono,
  redacted,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  redacted?: boolean;
}) {
  return (
    <div
      style={{
        padding: 10,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
          color: redacted ? "var(--hq-warn)" : "var(--hq-text)",
        }}
      >
        {value}
      </span>
      {redacted && (
        <HqBadge tone="warn" size="sm">
          REDACTED
        </HqBadge>
      )}
    </div>
  );
}

function GdprStrip({
  wasRedacted,
  redactedFields,
  redactedBy,
  redactedAt,
  consent,
  onOpenRedact,
}: {
  wasRedacted: boolean;
  redactedFields: RedactableFeedbackField[];
  redactedBy: string | null;
  redactedAt: number | null;
  consent: boolean;
  onOpenRedact: () => void;
}) {
  const tone = wasRedacted ? "warn" : consent ? "success" : "neutral";
  const color =
    tone === "warn"
      ? "var(--hq-warn)"
      : tone === "success"
        ? "var(--hq-success)"
        : "var(--hq-text-muted)";
  const bg =
    tone === "warn"
      ? "var(--hq-warn-soft)"
      : tone === "success"
        ? "var(--hq-success-soft)"
        : "var(--hq-bg-input)";
  const border =
    tone === "warn"
      ? "rgba(251,191,36,.25)"
      : tone === "success"
        ? "rgba(52,211,153,.25)"
        : "var(--hq-border-input)";
  return (
    <div
      style={{
        margin: "0 18px 16px",
        padding: "10px 12px",
        borderRadius: "var(--hq-radius-md)",
        background: bg,
        border: `1px solid ${border}`,
        color,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 11.5,
      }}
    >
      <Icons.Shield width={12} height={12} sw={1.8} />
      <div style={{ flex: 1, lineHeight: 1.45 }}>
        {wasRedacted ? (
          <>
            Redacted {redactedFields.join(", ")} by{" "}
            <span className="hq-mono">{redactedBy ?? "—"}</span>
            {redactedAt ? ` · ${new Date(redactedAt).toISOString().slice(0, 10)}` : ""}.
          </>
        ) : consent ? (
          <>System-info consent: opted in. Meta grid above shows OS + instance count.</>
        ) : (
          <>
            System-info consent: <strong>not opted in</strong>. OS + instance count omitted at
            ingest (spec §5.6).
          </>
        )}
      </div>
      <HqButton size="sm" variant="ghost" leadingIcon={Icons.EyeOff} onClick={onOpenRedact}>
        Redact fields
      </HqButton>
    </div>
  );
}

function ReplyComposer({
  value,
  onChange,
  busy,
  onSend,
}: {
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  onSend: () => void;
}) {
  return (
    <div
      style={{
        marginTop: "auto",
        padding: "12px 18px 18px",
        borderTop: "1px solid var(--hq-border)",
        background: "var(--hq-bg-raised)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Draft a reply… (V1 records intent + audit; email transport ships later)"
        rows={3}
        style={{
          width: "100%",
          background: "var(--hq-bg-input)",
          color: "var(--hq-text)",
          border: "1px solid var(--hq-border-input)",
          borderRadius: "var(--hq-radius-lg)",
          padding: "10px 12px",
          fontSize: 12.5,
          fontFamily: "var(--hq-font-sans)",
          resize: "vertical",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
        <HqIconButton
          icon={Icons.Image}
          size="sm"
          title="Insert template (coming in 47)"
          disabled
        />
        <HqButton
          variant="primary"
          size="sm"
          leadingIcon={Icons.Send}
          onClick={onSend}
          loading={busy}
        >
          Send reply
        </HqButton>
      </div>
    </div>
  );
}
